/* eslint-disable promise/prefer-await-to-callbacks */
import { DialogVariantEnum } from 'enums/dialogVariant';
import { PublishedStateEnum } from 'enums/publishedStateEnum';
import { SearchByEnum } from 'enums/searchByEnum';
import { combineEpics, Epic, ofType, StateObservable } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import store, { IStoreState } from 'store';
import { dialogOpen } from 'store/dialog/dialog.actions';
import { api } from 'utils/axiosInstance';
import { parseTaxonomiesFilter } from 'utils/formatters/parseTaxonomies';
import { apiErrorHandler, notify } from 'utils/handlers/notificationHandler';
import { TranslateRequestDTO } from 'utils/restApplicationClient';
import {
  addToQueue,
  addToQueueError,
  addToQueueSuccess,
  clearSelectedEntries,
  getContent,
  getContentError,
  getContentSuccess,
  getContentTypes,
  getContentTypesError,
  getContentTypesSuccess,
  getTaxonomy,
  getTaxonomyError,
  getTaxonomySuccess,
  getWorkflowSteps,
  getWorkflowStepsError,
  getWorkflowStepsSuccess,
  sendForTranslation,
  sendForTranslationError,
  sendForTranslationSuccess,
  setPagination,
} from './content.actions';
import { IContentPagination } from './content.interface';

const getContentEpic: Epic = (action$, store$: StateObservable<IStoreState>) =>
  action$.pipe(
    ofType(getContent.type, setPagination.type),
    switchMap(({ payload }: { payload: IContentPagination }) => {
      const {
        workflowStep,
        contentType,
        publishedState,
        page,
        size,
        orderBy,
        order,
        search,
        searchBy,
        languageCodeName,
        taxonomies,
      } = payload;
      const {
        content: { workflowSteps: allWorkflowSteps },
        auth: { projectId },
      } = store$.value;

      let workflowSteps: string[] = [];
      workflowSteps = workflowStep.map((workflowStep) => workflowStep.codeName);

      if (publishedState.includes(PublishedStateEnum.PUBLISHED)) {
        workflowSteps.push('published');
      }
      if (publishedState.includes(PublishedStateEnum.UNPUBLISHED)) {
        workflowSteps.push('archived');
      }
      if (publishedState.includes(PublishedStateEnum.NOT_PUBLISHED_YET)) {
        workflowSteps = [
          ...workflowSteps,
          ...allWorkflowSteps
            .filter((workflowStep) => workflowStep.codeName !== 'published' && workflowStep.codeName !== 'archived')
            .map((workflowStep) => workflowStep.codeName),
        ];
      }
      // eslint-disable-next-line unicorn/prefer-spread
      workflowSteps = Array.from(new Set(workflowSteps));

      return from(
        api.getItems(
          projectId || '',
          {
            name: search && searchBy === SearchByEnum.NAME ? search : undefined,
            codeName: search && searchBy === SearchByEnum.CODE_NAME ? search : undefined,
            languageCodeName: languageCodeName,
            workflows: workflowSteps,
            types: contentType.map((contentType) => contentType.codeName),
            taxonomies: parseTaxonomiesFilter(taxonomies),
          },
          {
            page: page,
            size: size,
            sort: `${orderBy},${order}`,
          },
        ),
      ).pipe(
        switchMap(({ data }) => {
          return of(
            getContentSuccess({
              data: data.content,
              totalPages: data.totalPages,
            }),
          );
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getContentError());
        }),
      );
    }),
  );

const addToQueueEpic: Epic = (action$) =>
  action$.pipe(
    ofType(addToQueue.type),
    switchMap(({ payload }: { payload: TranslateRequestDTO }) =>
      from(api.addToQueue(payload)).pipe(
        switchMap(() => {
          const { customerId, targetLanguages, sourceLanguage } = payload;
          notify('successmessage.addToQueue', 'success');
          return of(
            addToQueueSuccess({
              customerId: Number(customerId),
              sourceLanguage,
              targetLanguages,
            }),
            clearSelectedEntries(),
          );
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(addToQueueError());
        }),
      ),
    ),
  );

const sendForTranslationEpic: Epic = (action$) =>
  action$.pipe(
    ofType(sendForTranslation.type),
    switchMap(({ payload }: { payload: TranslateRequestDTO }) =>
      from(api.translate(payload)).pipe(
        switchMap(({ data }) => {
          const { customerId, targetLanguages, sourceLanguage } = payload;

          notify('successmessage.sendForTranslation', 'success');

          if (data.length >= 1) {
            store.dispatch(
              dialogOpen({
                variant: DialogVariantEnum.NO_SUPPORTED_ELEMENT,
                projectInfo: { jobs: data.map(({ id, productName }) => ({ id, name: productName })) },
              }),
            );
          }

          return of(
            sendForTranslationSuccess({
              customerId: Number(customerId),
              sourceLanguage,
              targetLanguages,
            }),
            clearSelectedEntries(),
          );
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(sendForTranslationError());
        }),
      ),
    ),
  );

const getContentTypesEpic: Epic = (action$, store$: StateObservable<IStoreState>) =>
  action$.pipe(
    ofType(getContentTypes.type),
    switchMap(() =>
      from(api.getProjectTypes(store$.value?.auth?.projectId || '')).pipe(
        switchMap(({ data }) => {
          return of(getContentTypesSuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getContentTypesError());
        }),
      ),
    ),
  );

const getWorkflowStepsEpic: Epic = (action$, store$: StateObservable<IStoreState>) =>
  action$.pipe(
    ofType(getWorkflowSteps.type),
    switchMap(() =>
      from(api.getProjectWorkflows(store$.value?.auth?.projectId || '')).pipe(
        switchMap(({ data }) => {
          return of(getWorkflowStepsSuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getWorkflowStepsError());
        }),
      ),
    ),
  );

const getTaxonomyEpic: Epic = (action$, store$: StateObservable<IStoreState>) =>
  action$.pipe(
    ofType(getTaxonomy.type),
    switchMap(() => {
      const { projectId } = store$.value.auth;
      return from(api.getTaxonomyListByKontentProject(projectId)).pipe(
        switchMap(({ data }) => {
          return of(getTaxonomySuccess(data));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getTaxonomyError());
        }),
      );
    }),
  );

export const contentEpics = combineEpics(
  getContentEpic,
  addToQueueEpic,
  sendForTranslationEpic,
  getContentTypesEpic,
  getWorkflowStepsEpic,
  getTaxonomyEpic,
);
